import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { User, Mail, Phone, Search, Building, Briefcase, FileText, Award, X, ChevronRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const VideoHeader = ({ title, subtitle }) => (
    <div className="h-screen w-full relative overflow-hidden">
        <video
            className="absolute top-1/2 left-1/2 w-full h-full object-cover transform -translate-x-1/2 -translate-y-1/2"
            src="/fenfakultesivideo.mp4"
            autoPlay
            loop
            muted
            playsInline
        />
        <div className="absolute inset-0 bg-black/60"></div>
        <div className="relative z-10 h-full flex flex-col items-center justify-center text-center px-4">
            <motion.h1 
                className="text-4xl md:text-7xl font-black text-white uppercase tracking-wider" 
                style={{ textShadow: '0px 4px 15px rgba(0,0,0,0.5)' }}
                initial={{ opacity: 0, y: -40 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
            >
                {title}
            </motion.h1>
            {subtitle && (
                <motion.p 
                    className="mt-5 text-base md:text-lg text-white/80 font-light max-w-3xl" 
                    style={{ textShadow: '0px 2px 8px rgba(0,0,0,0.5)' }}
                    initial={{ opacity: 0, y: 40 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 1, delay: 0.3, ease: [0.22, 1, 0.36, 1] }}
                >
                    {subtitle}
                </motion.p>
            )}
        </div>
    </div>
);

const AdministrativeStaff = () => {
    const { t } = useTranslation();
    const [searchTerm, setSearchTerm] = useState('');
    const [activeTab, setActiveTab] = useState('dekanlık');
    const [selectedPerson, setSelectedPerson] = useState(null);

    const deanshipStaff = t('administrativeStaff.deanshipStaff', { returnObjects: true });
    const departmentGroups = t('administrativeStaff.departmentGroups', { returnObjects: true });

    const getColorClasses = (color) => {
        const colors = {
            blue: 'from-blue-500/10 to-blue-600/10 border-blue-200 hover:from-blue-500/20 hover:to-blue-600/20',
            indigo: 'from-indigo-500/10 to-indigo-600/10 border-indigo-200 hover:from-indigo-500/20 hover:to-indigo-600/20',
            green: 'from-green-500/10 to-green-600/10 border-green-200 hover:from-green-500/20 hover:to-green-600/20',
            purple: 'from-purple-500/10 to-purple-600/10 border-purple-200 hover:from-purple-500/20 hover:to-purple-600/20',
            teal: 'from-teal-500/10 to-teal-600/10 border-teal-200 hover:from-teal-500/20 hover:to-teal-600/20',
            orange: 'from-orange-500/10 to-orange-600/10 border-orange-200 hover:from-orange-500/20 hover:to-orange-600/20',
            gray: 'from-gray-500/10 to-gray-600/10 border-gray-200 hover:from-gray-500/20 hover:to-gray-600/20'
        };
        return colors[color] || colors.gray;
    };
    
    const getIconBgColor = (color) => {
        const colors = {
            blue: 'bg-blue-500',
            indigo: 'bg-indigo-500',
            green: 'bg-green-500',
            purple: 'bg-purple-500',
            teal: 'bg-teal-500',
            orange: 'bg-orange-500',
            gray: 'bg-gray-500'
        };
        return colors[color] || colors.gray;
    };
    
    const filteredDeanshipStaff = deanshipStaff.filter(staff => {
        const matchesSearch = staff.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             staff.unit.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             staff.role.toLowerCase().includes(searchTerm.toLowerCase()) ||
                             staff.expertise.some(exp => exp.toLowerCase().includes(searchTerm.toLowerCase()));
        return matchesSearch;
    });
    
    const filteredDepartmentGroups = Object.entries(departmentGroups).reduce((acc, [dept, staff]) => {
        const filteredStaff = staff.filter(person => 
            person.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            dept.toLowerCase().includes(searchTerm.toLowerCase()) ||
            person.expertise.some(exp => exp.toLowerCase().includes(searchTerm.toLowerCase()))
        );
        if (filteredStaff.length > 0) {
            acc[dept] = filteredStaff;
        }
        return acc;
    }, {});

    return (
        <div className="bg-gray-50">
            <VideoHeader
                title={t('administrativeStaff.header.title')}
                subtitle={t('administrativeStaff.header.subtitle')}
            />
            <div className="relative bg-gray-50 -mt-20 rounded-t-3xl pt-16 pb-20 z-20">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    {/* Search and Tabs */}
                    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 mb-8 backdrop-blur-xl">
                        <div className="flex flex-col lg:flex-row gap-6">
                        <div className="flex-1 relative">
                            <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                            <Search className="w-5 h-5 text-gray-400" />
                            </div>
                            <input
                            type="text"
                            placeholder={t('administrativeStaff.searchPlaceholder')}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="w-full pl-12 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                            />
                        </div>
                        
                        <div className="flex bg-gray-50 rounded-xl p-1">
                            <button
                            onClick={() => setActiveTab('dekanlık')}
                            className={`px-6 py-3 rounded-lg font-medium transition-all ${
                                activeTab === 'dekanlık'
                                ? 'bg-white text-blue-600 shadow-lg ring-1 ring-blue-100'
                                : 'text-gray-600 hover:text-blue-600'
                            }`}
                            >
                            {t('administrativeStaff.tabs.deanship')}
                            </button>
                            <button
                            onClick={() => setActiveTab('bölümler')}
                            className={`px-6 py-3 rounded-lg font-medium transition-all ${
                                activeTab === 'bölümler'
                                ? 'bg-white text-blue-600 shadow-lg ring-1 ring-blue-100'
                                : 'text-gray-600 hover:text-blue-600'
                            }`}
                            >
                            {t('administrativeStaff.tabs.departments')}
                            </button>
                        </div>
                        </div>
                    </div>
                    {/* Dekanlık Personeli */}
                    {activeTab === 'dekanlık' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-12">
                            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
                                {filteredDeanshipStaff.map((staff) => (
                                <div 
                                    key={staff.id}
                                    className={`group bg-gradient-to-br ${getColorClasses(staff.color)} rounded-2xl border backdrop-blur-xl p-6 transition-all duration-300 hover:shadow-xl relative overflow-hidden cursor-pointer`}
                                    onClick={() => setSelectedPerson({...staff, department: t('administrativeStaff.tabs.deanship')})}
                                >
                                    <div className="absolute inset-0 opacity-[0.03] group-hover:opacity-[0.05] transition-opacity" style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg width='20' height='20' viewBox='0 0 20 20' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='%23000000' fill-opacity='1' fill-rule='evenodd'%3E%3Ccircle cx='3' cy='3' r='1'/%3E%3C/g%3E%3C/svg%3E")`, backgroundSize: '15px 15px' }} />
                                    <div className="relative">
                                    <div className="flex items-start gap-4 mb-6">
                                        <div className={`w-14 h-14 ${getIconBgColor(staff.color)} rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-transform`}>
                                        {staff.photo ? (
                                            <img src={staff.photo} alt={staff.name} className="w-full h-full object-cover rounded-xl" />
                                        ) : (
                                            <User className="w-7 h-7 text-white" />
                                        )}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                        <h3 className="font-bold text-gray-900 text-lg mb-1 group-hover:text-blue-700 transition-colors">{staff.name}</h3>
                                        <p className="text-sm text-gray-600 bg-white/60 backdrop-blur-sm px-2 py-1 rounded-md inline-block">{staff.role}</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-1 gap-4 mb-6">
                                        <div className="bg-white/60 backdrop-blur-sm rounded-lg p-3"><p className="text-xs text-gray-500 mb-1">{t('administrativeStaff.unit')}</p><p className="text-sm font-medium text-gray-900">{staff.unit}</p></div>
                                    </div>
                                    <div className="absolute bottom-0 right-0 p-3 opacity-0 group-hover:opacity-100 transition-opacity"><ChevronRight className="w-5 h-5 text-gray-400" /></div>
                                    </div>
                                </div>
                                ))}
                            </div>
                        </motion.div>
                    )}
                    {/* Bölüm Personeli */}
                    {activeTab === 'bölümler' && (
                        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-8">
                            {Object.entries(filteredDepartmentGroups).map(([department, staff]) => (
                                <div key={department} className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden transform hover:scale-[1.02] transition-transform">
                                    <div className="bg-gradient-to-r from-gray-50 via-blue-50 to-indigo-50 px-6 py-4 border-b border-gray-100">
                                        <div className="flex items-center justify-between">
                                            <div className="flex items-center gap-4">
                                                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg"><span className="text-2xl">{staff[0]?.emoji}</span></div>
                                                <div><h3 className="text-xl font-bold text-gray-900">{t('administrativeStaff.departmentOf')} {department}</h3><p className="text-sm text-gray-500">{t('administrativeStaff.administrativeStaff')}</p></div>
                                            </div>
                                            <span className="bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700 text-sm px-4 py-2 rounded-full font-medium">{staff.length} {t('administrativeStaff.staff')}</span>
                                        </div>
                                    </div>
                                    <div className="p-6">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                                        {staff.map((person) => (
                                            <div key={person.id} className="group bg-gradient-to-br from-gray-50 to-blue-50 rounded-xl p-4 hover:from-blue-50 hover:to-indigo-50 transition-all duration-300 border border-gray-100 hover:border-blue-200 hover:shadow-lg cursor-pointer h-full" onClick={() => setSelectedPerson({...person, department})}>
                                                <div className="flex items-center gap-4 h-full">
                                                    <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-lg flex-shrink-0 flex items-center justify-center shadow-md group-hover:scale-110 transition-transform">
                                                        <User className="w-6 h-6 text-white" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <h4 className="font-semibold text-gray-900 truncate group-hover:text-blue-700 transition-colors">{person.name}</h4>
                                                        <div className="flex items-center text-gray-500 text-sm mt-1">
                                                            <Phone className="w-4 h-4 mr-2 flex-shrink-0" />
                                                            <span>{person.phone}</span>
                                                        </div>
                                                </div>
                                                </div>
                                            </div>
                                        ))}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </motion.div>
                    )}
                    {/* No results */}
                    {((activeTab === 'dekanlık' && filteredDeanshipStaff.length === 0) || (activeTab === 'bölümler' && Object.keys(filteredDepartmentGroups).length === 0)) && (
                        <div className="text-center py-16"><div className="w-24 h-24 bg-gradient-to-br from-gray-100 to-gray-200 rounded-full flex items-center justify-center mx-auto mb-6 animate-pulse"><FileText className="w-12 h-12 text-gray-400" /></div><h3 className="text-xl font-semibold text-gray-900 mb-2">{t('administrativeStaff.noStaffFound')}</h3><p className="text-gray-600">{t('administrativeStaff.noStaffFoundMessage')}</p></div>
                    )}
                </div>
            </div>

            {selectedPerson && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 flex items-center justify-center p-4">
                  <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={() => setSelectedPerson(null)} />
                  <motion.div initial={{ scale: 0.9, y: 20 }} animate={{ scale: 1, y: 0 }} className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
                    <div className="p-8">
                      <button onClick={() => setSelectedPerson(null)} className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"><X className="w-6 h-6" /></button>
                      <div className="flex items-start gap-6 mb-8">
                        <div className="flex items-center gap-3">
                            <div className={`w-12 h-12 ${getIconBgColor(selectedPerson.color)} rounded-lg flex items-center justify-center shadow-md`}><User className="w-6 h-6 text-white" /></div>
                            <div className="flex-1 min-w-0">
                                <h3 className="text-xl font-bold text-gray-900 truncate">{selectedPerson.name}</h3>
                                <p className="text-gray-500">{selectedPerson.department}</p>
                          </div>
                        </div>
                        {selectedPerson.role && (
                            <div className="mt-4">
                                <span className="bg-blue-100 text-blue-800 text-sm font-medium px-3 py-1 rounded-full">{selectedPerson.role}</span>
                            </div>
                        )}
                      </div>
                      <div className="p-6">
                        <h4 className="font-medium text-gray-800">{t('administrativeStaff.contact')}</h4>
                        <div className="mt-2 space-y-2 text-gray-600">
                            <div className="flex items-center gap-3"><Mail className="w-4 h-4 text-gray-400" />{selectedPerson.email}</div>
                            <div className="flex items-center gap-3"><Phone className="w-4 h-4 text-gray-400" />{selectedPerson.phone}</div>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                </motion.div>
              )}
        </div>
    );
};

export default AdministrativeStaff;